// src/pages/commercial/CommercialResourcesPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';

export default function CommercialResourcesPage() {
    const { user } = useAuth();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [category, setCategory] = useState('all');

    // Filter states
    const categories = [
        { id: 'all', label: 'Tout' },
        { id: 'marketing', label: 'Marketing' },
        { id: 'pricing', label: 'Tarifs' },
        { id: 'legal', label: 'Juridique' },
        { id: 'tech', label: 'Technique' },
        { id: 'other', label: 'Autre' }
    ];

    const [formData, setFormData] = useState({
        title: '',
        category: 'marketing',
        file: null,
        description: ''
    });

    const fetchResources = async () => {
        setLoading(true);
        try {
            const res = await pb.collection('resources').getFullList({
                sort: '-created'
            });
            setResources(res);
        } catch (err) {
            console.error("Error fetching resources (Collection 'resources' might be missing)", err);
            // Don't alert here to avoid spamming if collection is missing, just show empty
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFormData({ ...formData, file: e.target.files[0] });
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!formData.file) {
            alert("Veuillez sélectionner un fichier.");
            return;
        }
        setUploading(true);
        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('category', formData.category);
            data.append('description', formData.description);
            data.append('file', formData.file);
            data.append('owner', user.id);

            await pb.collection('resources').create(data);
            setShowModal(false);
            setFormData({ title: '', category: 'marketing', file: null, description: '' });
            fetchResources();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de l'upload. Vérifiez que la collection 'resources' existe dans PocketBase.");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce document ?")) return;
        try {
            await pb.collection('resources').delete(id);
            setResources(prev => prev.filter(r => r.id !== id));
        } catch (err) {
            alert("Erreur suppression");
        }
    };

    const filteredResources = resources.filter(r => category === 'all' || r.category === category);

    const getFileIcon = (filename) => {
        if (!filename) return '📄';
        const ext = filename.split('.').pop().toLowerCase();
        if (['pdf'].includes(ext)) return '📕';
        if (['doc', 'docx'].includes(ext)) return '📘';
        if (['xls', 'xlsx'].includes(ext)) return '📗';
        if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return '🖼️';
        if (['zip', 'rar'].includes(ext)) return '📦';
        return '📄';
    };

    return (
        <CommercialLayout title="Ressources">
            <div className="commercial-page-wrapper">
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }

                .commercial-page-wrapper .btn-primary { 
                    display: inline-flex; align-items: center; gap: 10px;
                    padding: 10px 14px; border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-weight: 500;
                }
                .commercial-page-wrapper .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
                
                .commercial-page-wrapper .resource-filters {
                    display: flex; gap: 10px; margin-bottom: 24px; overflow-x: auto; padding-bottom: 4px;
                }
                .commercial-page-wrapper .filter-tab {
                    padding: 8px 16px; border-radius: 99px; cursor: pointer;
                    font-weight: 500; border: 1px solid var(--admin-border);
                    background: var(--admin-panel); color: var(--admin-muted);
                    white-space: nowrap; transition: all 0.2s;
                }
                .commercial-page-wrapper .filter-tab:hover {
                    background: var(--admin-panel-2);
                }
                .commercial-page-wrapper .filter-tab.active {
                    background: var(--admin-accent);
                    color: white;
                    border-color: var(--admin-accent);
                }

                .commercial-page-wrapper .resources-grid {
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px;
                }
                .commercial-page-wrapper .resource-card {
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    border-radius: 16px;
                    padding: 20px;
                    display: flex; flex-direction: column; gap: 12px;
                    transition: all 0.2s;
                }
                .commercial-page-wrapper .resource-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px -5px rgba(0,0,0,0.05);
                }
                .commercial-page-wrapper .resource-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: var(--admin-panel-2);
                    display: grid; place-items: center;
                    font-size: 1.5rem;
                }
                .commercial-page-wrapper .download-btn {
                    margin-top: auto;
                    display: block; text-align: center;
                    padding: 8px; border-radius: 8px;
                    background: var(--admin-panel-2);
                    color: var(--admin-accent);
                    font-weight: 500;
                    text-decoration: none;
                    transition: background 0.2s;
                }
                .commercial-page-wrapper .download-btn:hover {
                    background: rgba(79, 70, 229, 0.1);
                }

                 /* Modal */
                .commercial-page-wrapper .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; backdrop-filter: blur(2px); }
                .commercial-page-wrapper .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 16px; width: 100%; max-width: 500px; padding: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .commercial-page-wrapper .form-group { margin-bottom: 16px; }
                .commercial-page-wrapper .form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 0.9rem; }
                .commercial-page-wrapper .form-control { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); font-size: 0.95rem; }

                `}</style>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Ressources</h1>
                        <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0' }}>Tous vos outils et documents</p>
                    </div>
                    <button className="btn-primary" onClick={() => setShowModal(true)}>
                        <span>📤 Ajouter un document</span>
                    </button>
                </div>

                <div className="resource-filters">
                    {categories.map(cat => (
                        <div
                            key={cat.id}
                            className={`filter-tab ${category === cat.id ? 'active' : ''}`}
                            onClick={() => setCategory(cat.id)}
                        >
                            {cat.label}
                        </div>
                    ))}
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement...</div>
                ) : (
                    <div className="resources-grid">
                        {filteredResources.length === 0 ? (
                            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: 'var(--admin-muted)', border: '1px dashed var(--admin-border)', borderRadius: 16 }}>
                                Aucun document dans cette catégorie.
                            </div>
                        ) : (
                            filteredResources.map(res => (
                                <div key={res.id} className="resource-card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div className="resource-icon">{getFileIcon(res.file)}</div>
                                        {user.id === res.owner && (
                                            <button onClick={() => handleDelete(res.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.3 }}>✕</button>
                                        )}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{res.title}</div>
                                        <div style={{ fontSize: '0.85rem', color: 'var(--admin-muted)', marginTop: 4 }}>
                                            {res.description || categories.find(c => c.id === res.category)?.label}
                                        </div>
                                    </div>
                                    <a
                                        href={pb.files.getUrl(res, res.file)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="download-btn"
                                    >
                                        Télécharger / Voir
                                    </a>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Modal Upload */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <h2 style={{ marginTop: 0, marginBottom: 20 }}>Ajouter un document</h2>
                            <form onSubmit={handleUpload}>
                                <div className="form-group">
                                    <label>Titre</label>
                                    <input required type="text" className="form-control" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Plaquette Commerciale 2026" />
                                </div>
                                <div className="form-group">
                                    <label>Catégorie</label>
                                    <select className="form-control" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                                        {categories.filter(c => c.id !== 'all').map(c => (
                                            <option key={c.id} value={c.id}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fichier</label>
                                    <input required type="file" className="form-control" onChange={handleFileChange} />
                                </div>
                                <div className="form-group">
                                    <label>Description (optionnel)</label>
                                    <textarea className="form-control" rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                                    <button type="button" onClick={() => setShowModal(false)} style={{ background: 'transparent', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
                                    <button type="submit" disabled={uploading} className="btn-primary" style={{ border: 'none' }}>
                                        {uploading ? 'Envoi...' : 'Ajouter'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}
